import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import 'mantine-react-table/styles.css';

import {
    ActionsColumn,
    ApnCountColumn,
    CarrierColumn,
    IdColumn,
    UpstreamCarrierColumn,
} from './columns';
import {
    Box,
    Container,
    Flex,
    Group,
    MultiSelect,
    Portal,
    Select,
    Text,
} from '@mantine/core';
import {
    useDebouncedCallback,
    useDisclosure,
    useLocalStorage,
} from '@mantine/hooks';
import { useMemo, useState } from 'react';

import FiltersMultiSelect from '../../../common/filtersMultiSelect/FiltersMultiSelect';
import JsonOptionsColumn from '../../../common/tables/columns/JsonOptionsColumn';
import JsonOptionsModal from '../../../modals/options/JsonOptionsModal';
import { MantineReactTable } from 'mantine-react-table';
import { getCarrierConfigs } from '../../../../http/apiClient';
import { usePaginatedQuery } from '../../../../http/queries/usePaginatedQuery';

const CarriersView = ({ ...props }) => {
    const [globalFilter, setGlobalFilter] = useState(null);
    const [sorting, setSorting] = useState([]);
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 50,
    });

    const [jsonModalProps, setJsonModalProps] = useState({});
    const [
        optionsModalOpen,
        { open: openOptionsModal, close: closeOptionsModal },
    ] = useDisclosure();

    const filterDebounce = useDebouncedCallback((query) => {
        setGlobalFilter(query);
    }, 600);

    const {
        isLoading,
        isError,
        error,
        data: paginatedData,
    } = usePaginatedQuery({
        queryFn: (queryParams) => getCarrierConfigs(queryParams),
        queryKey: ['carriers'],
        refetchInterval: 15_0000,
        globalFilter,
        pagination,
        sorting,
    });

    const handleShowOptions = (accessorKey, jsonObj) => {
        const optionTitles = {
            imsOptions: 'IMS Options',
            mmsOptions: 'MMS Options',
            campaignOptions: 'Campaign Options',
        };
        setJsonModalProps({
            header: optionTitles[accessorKey] || 'Options',
            jsonObj: jsonObj,
        });
        openOptionsModal();
    };

    const { data: carrierConfigs, pagination: dataPagination } =
        paginatedData || {};

    const columns = useMemo(
        () => [
            IdColumn(),
            CarrierColumn(),
            //TextColumn({header: 'Upstream Carrier', accessorKey: 'upstreamCarrier'}),
            JsonOptionsColumn({
                header: 'Campaign Options',
                id: 'campaignOptions',
                accessorKey: 'campaignOptions',
                onClick: handleShowOptions,
            }),
            JsonOptionsColumn({
                header: 'IMS Options',
                id: 'imsOptions',
                accessorKey: 'imsOptions',
                onClick: handleShowOptions,
            }),
            JsonOptionsColumn({
                header: 'MMS Options',
                id: 'mmsOptions',
                accessorKey: 'mmsOptions',
                onClick: handleShowOptions,
            }),
        ],
        [carrierConfigs]
    );

    const tableData = useMemo(
        () => (isLoading ? [] : carrierConfigs),
        [carrierConfigs, isLoading]
    );

    const [columnVisibility, setColumnVisibility] = useLocalStorage({
        key: 'carriers-table-visibility', // a unique key for local storage
        defaultValue: {}, // default to all columns shown if empty
        getInitialValueInEffect: true,
    });

    const [columnSizing, setColumnSizing] = useLocalStorage({
        key: 'carriers-group-table-sizing', // a unique key for local storage
        defaultValue: {}, // default to all columns shown if empty
        getInitialValueInEffect: true,
    });

    const handleVisibilityChange = (changeFunc) => {
        const changes = changeFunc();
        setColumnVisibility((prevState) => ({
            ...prevState,
            ...changes,
        }));
    };

    const handlSizeChange = (changeFunc) => {
        const changes = changeFunc();
        setColumnSizing((prevState) => ({
            ...prevState,
            ...changes,
        }));
    };

    const handleFilterChange = (queryStr) => {
        filterDebounce(queryStr);
    };

    const tableProps = {
        columns: columns,
        data: tableData,
        enableRowActions: false,
        enableColumnResizing: true,
        enableColumnActions: false,
        enableSorting: false,
        enableTopToolbar: true,
        enableGlobalFilter: true,
        enableFilters: false,
        enableStickyHeader: true,
        enableDensityToggle: false,
        positionActionsColumn: 'last',
        positionExpandColumn: 'last',
        memoMode: 'cell',
        layoutMode: 'semantic',
        mantineSearchTextInputProps: {
            placeholder: 'Search Carriers',
            miw: 200,
        },
        mantineTableContainerProps: {
            style: {
                minHeight: '80vh',
            },
        },
        positionGlobalFilter: 'left',
        renderTopToolbarCustomActions: () => (
            <Box
                w={'100%'}
                px={'lg'}>
                <FiltersMultiSelect
                    options={[{ label: 'test', value: 'test' }]}
                />
            </Box>
        ),
        //renderRowActions: ActionsColumn,

        manualPagination: true,
        manualSorting: true,
        onColumnVisibilityChange: handleVisibilityChange,
        onColumnSizingChange: handlSizeChange,
        onPaginationChange: setPagination,
        onSortingChange: setSorting,
        onGlobalFilterChange: handleFilterChange,
        rowCount: dataPagination?.totalItems || 0,
        pageCount: dataPagination?.totalPages || 1,

        initialState: {
            columnVisibility,
            pagination,
            isLoading,
            showGlobalFilter: true,
        },
        state: {
            isLoading,
            pagination,
            columnVisibility,
            columnSizing,
            showAlertBanner: isError,
            showProgressBars: isLoading,
            sorting,
            density: 'xs',
        },
    };

    return (
        <Container fluid>
            <MantineReactTable {...tableProps} />
            <JsonOptionsModal
                {...jsonModalProps}
                opened={optionsModalOpen}
                close={closeOptionsModal}
            />
        </Container>
    );
};
export default CarriersView;
